import React, { useState, useEffect, useRef } from 'react';
import { Target, Users, AlertTriangle, Calendar } from 'lucide-react';
import { mockData } from '../mock';

const Stats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({});
  const sectionRef = useRef(null);

  const iconMap = {
    Target,
    Users,
    AlertTriangle,
    Calendar
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      // Animate counters
      mockData.stats.forEach((stat, index) => {
        const targetValue = parseInt(stat.value.replace(/\D/g, ''));
        let currentValue = 0;
        const increment = targetValue / 50;
        const suffix = stat.value.replace(/\d/g, '');

        const timer = setInterval(() => {
          currentValue += increment;
          if (currentValue >= targetValue) {
            currentValue = targetValue;
            clearInterval(timer);
          }
          
          setCounters(prev => ({
            ...prev,
            [index]: Math.floor(currentValue) + suffix
          }));
        }, 40);
      });
    }
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-black to-gray-900">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {mockData.stats.map((stat, index) => {
            const IconComponent = iconMap[stat.icon];
            
            return (
              <div
                key={index}
                className={`text-center group ${
                  isVisible ? 'animate-fadeInUp' : 'opacity-0'
                }`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="relative mb-4">
                  {/* Icon Container */}
                  <div className="inline-flex items-center justify-center w-16 h-16 mx-auto glass-effect border border-cyan-400/30 rounded-full group-hover:border-cyan-400 transition-all duration-500">
                    <IconComponent className="w-8 h-8 text-cyan-400 group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  
                  {/* Glow Effect */}
                  <div className="absolute inset-0 w-16 h-16 mx-auto bg-cyan-400/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>

                {/* Counter */}
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                    {counters[index] || stat.value}
                  </span>
                </div>

                {/* Label */}
                <div className="text-gray-400 font-medium group-hover:text-gray-300 transition-colors duration-300">
                  {stat.label}
                </div>

                {/* Animated Border */}
                <div className="mt-4 h-1 w-full bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000"
                    style={{ animationDelay: `${index * 0.3}s` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Background Effect */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Stats;